import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface Dimensions {
  width: number;
  height: number;
  top: number;
  bottom: number;
  left: number;
  right: number;
  x: number;
  y: number;
}

interface UseDimensionsOptions {
  onDimensions?: (dimensions: Dimensions) => void;
}

export function useDimensions<T extends HTMLElement>(options?: UseDimensionsOptions) {
  const ref = useRef<T>(null);
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: 0,
    height: 0,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    x: 0,
    y: 0,
  });

  const onDimensions = useCallback(
    (d: Dimensions) => {
      if (!options?.onDimensions) return;

      options.onDimensions(d);
    },
    [options],
  );

  const measure = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const d = {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        x: rect.x,
        y: rect.y,
      };

      setDimensions(d);
      onDimensions(d);
    });
  }, [ref, onDimensions]);

  useLayoutEffect(() => {
    if (ref.current) {
      measure();
    }
  }, [measure]);

  return [ref, dimensions, measure] as const;
}

/**
 * Tracks the content-box height of an element and keeps it up to date via a
 * `ResizeObserver`. Unlike a one-shot measurement, this stays correct when the
 * element reflows after its first layout — most importantly when the custom web
 * fonts load in. In production builds fonts are fetched over the network and swap
 * in *after* the initial measurement, so a one-shot read is stale (this doesn't
 * surface locally, where fonts are cached and StrictMode remounts re-measure).
 * A stale value here throws off any sticky offset derived from it.
 */
export function useContentBoxHeight<T extends HTMLElement>(ref: RefObject<T>) {
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHeight(entry.contentRect.height);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [ref]);

  return height;
}

/**
 * Tracks the content-box width of an element via a `ResizeObserver`. Same rationale
 * as `useContentBoxHeight`: a one-shot read goes stale once the element reflows.
 */
export function useContentBoxWidth<T extends HTMLElement>(ref: RefObject<T>) {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [ref]);

  return width;
}
